import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, ArrowLeft, BadgeIndianRupee, Check, HardHat, Pencil, Play, Ruler, Search,
  Timer, Trash2, TrendingUp, Wallet, X, Shirt, CheckCircle2,
} from 'lucide-react';
import { fetchWorkerOverview, saveWorkerRate, deleteWorkerRate } from '../api/resources';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { inr, fmtDate } from '../utils/format';
import Spinner from '../components/ui/Spinner';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { StatusBadge } from '../components/ui/Badge';

const JOB_LABEL = { ASSIGNED: 'Waiting', IN_PROGRESS: 'Working now', COMPLETED: 'Finished' };
const JOB_TONE = {
  ASSIGNED: 'bg-surface-sunken text-content-muted',
  IN_PROGRESS: 'bg-warning-50 text-warning-700',
  COMPLETED: 'bg-success-50 text-success-700',
};
const WORK_LABEL = { CUTTING: 'Cutting', STITCHING: 'Stitching' };

/**
 * One worker, end to end: who they are, what they are doing right now, what is
 * queued, everything they have finished, what has been paid, and their rate
 * card (editable by an admin).
 *
 * Money words match the settlement screens, so the same number is never
 * called two things:
 *   earned = finished work · paid = inside a posted settlement ·
 *   unpaid = finished but not settled · committed = queued / in progress.
 */
export default function WorkerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  // Back belongs to whoever sent you here — the workers list, or the
  // performance page's capacity table.
  const location = useLocation();
  const backTo = location.state?.from || '/workers';
  const backLabel = backTo === '/performance' ? 'Back to Performance' : 'Back to Workers';
  const { role } = useAuth();
  const isAdmin = role === 'ADMIN';
  // Two doors, not four tabs: the work they are doing, and the money.
  const [card, setCard] = useState(null); // 'work' | 'billing'

  const { data, isLoading, isError } = useQuery({
    queryKey: ['worker-overview', id],
    queryFn: () => fetchWorkerOverview(id),
  });

  if (isLoading) return <Spinner label="Loading worker…" />;
  if (isError || !data) {
    return (
      <div className="py-20 text-center">
        <p className="text-lg font-semibold text-content">Worker not found</p>
        <button className="btn-primary mt-4" onClick={() => nav(backTo)}>{backLabel}</button>
      </div>
    );
  }

  const { worker, totals, running, waiting, completed, settlements, rateCard, workerWiseRates, performance } = data;

  return (
    <div className="space-y-4">
      {/* ---- who ---- */}
      <div className="card mod-card flex flex-wrap items-center gap-3 p-4">
        <button className="btn-icon" title={backLabel} onClick={() => nav(backTo)}><ArrowLeft size={17} /></button>
        <span className="mod-icon flex h-11 w-11 items-center justify-center rounded-xl"><HardHat size={22} /></span>
        <div className="mr-auto min-w-0">
          <h1 className="truncate text-xl font-bold text-content">{worker.name}</h1>
          <p className="text-xs text-content-muted">
            {[worker.code && `Code ${worker.code}`, worker.role?.replace(/_/g, ' ').toLowerCase(), worker.phone, [worker.city, worker.state].filter(Boolean).join(', ')]
              .filter(Boolean).join(' · ')}
          </p>
        </div>
        <StatusBadge status={worker.status} />
        {worker.loginName && (
          <span className="rounded-full bg-brand-50 px-2.5 py-1 text-2xs font-semibold text-brand-700">
            Portal login: {worker.loginName}
          </span>
        )}
      </div>

      {/* ---- the choice ---- */}
      {!card && (
        <div className="grid gap-4 sm:grid-cols-2">
          <BigCard
            tone="brand"
            icon={Shirt}
            title="Work & job history"
            line={`${totals.counts.running + totals.counts.waiting} on hand · ${totals.counts.completed} finished`}
            blurb="What they are making right now, what is queued, and everything they have finished."
            onClick={() => setCard('work')}
          />
          <BigCard
            tone="success"
            icon={Wallet}
            title="Billing & statement"
            line={`${inr(totals.unpaid)} unpaid · ${inr(totals.paid)} paid`}
            blurb="What has been billed, what is still unbilled, and every payout slip so far."
            onClick={() => setCard('billing')}
          />
        </div>
      )}

      {card && (
        <button className="btn-ghost btn-sm" onClick={() => setCard(null)}>
          <ArrowLeft size={14} /> Back to {worker.name}'s cards
        </button>
      )}

      {/* ---- card 1: the work ---- */}
      {card === 'work' && (
        <WorkCard
          totals={totals}
          running={running}
          waiting={waiting}
          completed={completed}
          performance={performance}
        />
      )}

      {/* ---- card 2: the money ---- */}
      {card === 'billing' && (
        <div className="space-y-4">
          <Statement totals={totals} />
          <JobTable
            title="What the unpaid work is made of"
            hint="Finished jobs that are not on a payout slip yet."
            jobs={completed.filter((j) => !j.settled)}
            empty="Everything finished has been billed."
            finished
            money
          />
          <Payouts settlements={settlements} unpaid={totals.unpaid} />
          <RateCard
            rows={rateCard}
            workerId={worker.id}
            isAdmin={isAdmin}
            workerWiseRates={workerWiseRates}
          />
        </div>
      )}
    </div>
  );
}

const sumOf = (jobs) => jobs.reduce((s, j) => s + Number(j.cost || 0), 0);

const STAGE_WORDS = {
  RECEIVED: 'Just received',
  CUTTING: 'Being cut',
  CUTTING_DONE: 'Cut, waiting',
  ASSIGNED: 'Handed over',
  STITCHING: 'Being stitched',
  QC: 'With checking',
  ALTERATION: 'In alteration',
  READY: 'Ready',
  DELIVERED: 'Delivered',
};

/**
 * The owner's five questions about one person, in words rather than jargon:
 * how much is on their hands, how much is late, how long they take, how often
 * they finish in time, and where their work is stuck.
 */
/**
 * What this worker has on, in one row of cards — and one list underneath that
 * answers whichever card you tapped. Three stacked tables made you scroll past
 * two empty ones to reach the work you were looking for.
 */
function WorkCard({ totals, running, waiting, completed, performance }) {
  const [view, setView] = useState('running');
  const p = performance || {};
  const onTime = p.onTime || {};
  const timeTone = onTime.pct == null ? 'plain' : onTime.pct >= 85 ? 'success' : onTime.pct >= 60 ? 'warning' : 'danger';

  // Late is judged on the order's delivery date, the same way the floor does it.
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const onHands = [...running, ...waiting];
  const late = onHands.filter((jb) => jb.deliveryDate && new Date(jb.deliveryDate) < today);

  const VIEWS = {
    running: { title: 'Working on right now', hint: 'Started by the worker in their portal, or set here by an admin.', jobs: running, empty: 'Nothing in progress.' },
    waiting: { title: 'Waiting to start', hint: 'Handed to them, not begun yet.', jobs: waiting, empty: 'No queued work.' },
    completed: { title: 'Finished work', hint: 'Newest first. The money for these sits under Billing & statement.', jobs: completed, empty: 'No finished work yet.', finished: true },
    hands: { title: 'Everything on their hands', hint: 'Working on and waiting to start, together.', jobs: onHands, empty: 'Nothing with them right now.' },
    late: { title: 'Running late', hint: 'Past the delivery date on the order.', jobs: late, empty: 'Nothing is late.' },
  };
  const shown = VIEWS[view] || VIEWS.running;

  return (
    <div className="space-y-4">
      {/* One row; it wraps only when the screen is too narrow to hold it. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <Tile
          label="Working now" value={`${totals.counts.running}`} sub="jobs" icon={Play} tone="warning"
          active={view === 'running'} onClick={() => setView('running')}
        />
        <Tile
          label="Waiting to start" value={`${totals.counts.waiting}`} sub="jobs" icon={Ruler} tone="plain"
          active={view === 'waiting'} onClick={() => setView('waiting')}
        />
        <Tile
          label="Finished" value={`${totals.counts.completed}`} sub="jobs" icon={CheckCircle2} tone="success"
          active={view === 'completed'} onClick={() => setView('completed')}
        />
        <Tile
          label="On their hands" value={`${onHands.length}`}
          sub={p.orders != null ? `${p.orders} order${p.orders === 1 ? '' : 's'} · ${p.pieces} pc` : 'jobs'}
          icon={HardHat} tone="plain"
          active={view === 'hands'} onClick={() => setView('hands')}
        />
        <Tile
          label="Running late" value={`${late.length}`}
          sub={p.dueThisWeek ? `${p.dueThisWeek} due this week` : 'nothing else due'}
          icon={AlertTriangle} tone={late.length ? 'danger' : 'success'}
          active={view === 'late'} onClick={() => setView('late')}
        />
        <Tile
          label="Takes about" value={p.avgDays != null ? `${p.avgDays}d` : '—'}
          sub={p.sample ? `from ${p.sample} job${p.sample === 1 ? '' : 's'}` : 'no finished work'}
          icon={Timer} tone="plain"
        />
        <Tile
          label="Finishes in time" value={onTime.pct != null ? `${onTime.pct}%` : '—'}
          sub={onTime.judged ? `${onTime.onTimeCount} of ${onTime.judged}` : 'needs finished work'}
          icon={TrendingUp} tone={timeTone}
        />
      </div>

      {performance && <WhereItSits p={performance} />}

      <JobTable
        title={shown.title}
        hint={shown.hint}
        jobs={shown.jobs}
        empty={shown.empty}
        finished={shown.finished}
      />
    </div>
  );
}

function WhereItSits({ p }) {
  const peak = Math.max(1, ...(p.stages || []).map((x) => x.count));

  return (
    <div className="card p-4">
        <h2 className="text-xs font-bold uppercase tracking-wider text-content">Where their work is sitting</h2>
        <p className="mb-3 text-2xs text-content-subtle">
          {p.stuckAt
            ? `Most of it is ${(STAGE_WORDS[p.stuckAt.stage] || p.stuckAt.stage).toLowerCase()} — ${p.stuckAt.count} garment${p.stuckAt.count === 1 ? '' : 's'}.`
            : 'Nothing is with them right now.'}
        </p>
        {(p.stages || []).length === 0 ? (
          <p className="py-3 text-center text-sm italic text-content-subtle">No work in hand.</p>
        ) : (
          <div className="space-y-2">
            {p.stages.map((row) => (
              <div key={row.stage}>
                <div className="mb-0.5 flex items-baseline justify-between text-xs">
                  <span className="font-medium text-content">{STAGE_WORDS[row.stage] || row.stage}</span>
                  <span className="font-bold tabular-nums text-content">{row.count}</span>
                </div>
                <div className="h-2.5 overflow-hidden rounded-full bg-surface-sunken">
                  <div
                    className={`h-full rounded-full ${row.count === peak ? 'bg-warning-500' : 'bg-brand-500'}`}
                    style={{ width: `${Math.round((row.count / peak) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

/**
 * One of the two doors into a worker's page. Big, plain and unmissable: the
 * shop owner reads the line under the title, not a column header.
 */
function BigCard({ tone, icon: Icon, title, line, blurb, onClick }) {
  const tones = {
    brand: 'border-brand-500 bg-brand-50/40 text-brand-700',
    success: 'border-success-500 bg-success-50/40 text-success-700',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`card flex items-start gap-4 border-l-4 p-5 text-left transition-shadow hover:shadow-raised ${tones[tone]}`}
    >
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-surface shadow-xs">
        <Icon size={24} />
      </span>
      <span className="min-w-0">
        <span className="block text-lg font-bold text-content">{title}</span>
        <span className="block text-sm font-semibold">{line}</span>
        <span className="mt-1 block text-xs text-content-muted">{blurb}</span>
      </span>
    </button>
  );
}

/**
 * The statement, in the order a person asks the questions: what has this work
 * come to, how much of it has been billed, and what is still owed.
 */
function Statement({ totals }) {
  const row = (label, value, hint, strong) => (
    <div className="flex flex-wrap items-baseline gap-x-3 border-b border-line py-2 last:border-0">
      <span className={`text-sm ${strong ? 'font-bold text-content' : 'text-content-muted'}`}>{label}</span>
      {hint && <span className="text-2xs text-content-subtle">{hint}</span>}
      <span className={`ml-auto tabular-nums ${strong ? 'text-base font-bold text-content' : 'text-sm font-semibold text-content'}`}>
        {inr(value)}
      </span>
    </div>
  );
  return (
    <div className="card p-4">
      <h3 className="mb-1 text-xs font-bold uppercase tracking-wider text-content">Statement</h3>
      <p className="mb-2 text-2xs text-content-subtle">Every finished job, and where its money has got to.</p>
      {row('Billed', totals.billed ?? 0, 'on a payout slip')}
      {row('Unbilled', totals.unbilled ?? 0, 'finished, not on a slip yet')}
      {row('Total earned', totals.earned, 'all finished work', true)}
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-success-500 bg-success-50/40 p-3">
          <p className="text-2xs font-bold uppercase tracking-wider text-success-700">Paid</p>
          <p className="text-xl font-bold tabular-nums text-content">{inr(totals.paid)}</p>
        </div>
        <div className={`rounded-lg border p-3 ${totals.unpaid ? 'border-danger-500 bg-danger-50/40' : 'border-line'}`}>
          <p className={`text-2xs font-bold uppercase tracking-wider ${totals.unpaid ? 'text-danger-600' : 'text-content-subtle'}`}>Unpaid</p>
          <p className="text-xl font-bold tabular-nums text-content">{inr(totals.unpaid)}</p>
        </div>
      </div>
      {totals.committed > 0 && (
        <p className="mt-2 text-xs text-content-muted">
          Another <b className="tabular-nums text-content">{inr(totals.committed)}</b> is on work they are still doing.
        </p>
      )}
    </div>
  );
}

function Tile({ label, value, sub, icon: Icon, tone, active, onClick }) {
  const tones = {
    warning: 'text-warning-700 bg-warning-50',
    success: 'text-success-700 bg-success-50',
    danger: 'text-danger-600 bg-danger-50',
    plain: 'text-content-muted bg-surface-sunken',
  };
  const body = (
    <>
      <div className="flex items-center gap-2">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${tones[tone]}`}><Icon size={15} /></span>
        <p className="truncate text-2xs font-bold uppercase tracking-wider text-content-subtle">{label}</p>
      </div>
      <p className="mt-1.5 text-xl font-bold tabular-nums text-content">{value}</p>
      {sub && <p className="truncate text-xs text-content-muted" title={sub}>{sub}</p>}
    </>
  );
  if (!onClick) return <div className="card p-3">{body}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Show ${label.toLowerCase()} below`}
      className={`card p-3 text-left transition-shadow hover:shadow-raised ${
        active ? 'border-brand-600 ring-2 ring-brand-500/30' : ''
      }`}
    >
      {body}
    </button>
  );
}

/* ---------------- jobs ---------------- */
function JobTable({ title, hint, jobs, empty, finished, money }) {
  const nav = useNavigate();
  return (
    <section className="card overflow-hidden">
      <header className="border-b border-line px-3 py-2">
        <h2 className="text-xs font-bold uppercase tracking-wider text-content">{title}</h2>
        {hint && <p className="text-2xs text-content-subtle">{hint}</p>}
      </header>
      {jobs.length === 0 ? (
        <p className="py-6 text-center text-sm italic text-content-subtle">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-sunken text-left text-2xs font-bold uppercase tracking-wide text-content-muted">
              <tr>
                <th className="px-3 py-1.5">Order</th>
                <th className="px-3 py-1.5">Customer</th>
                <th className="px-3 py-1.5">Item</th>
                <th className="px-3 py-1.5">Work</th>
                <th className="px-3 py-1.5">Job status</th>
                <th className="px-3 py-1.5">{finished ? 'Finished' : 'Delivery'}</th>
                {money && <th className="px-3 py-1.5 text-right">Pay</th>}
                {money && <th className="px-3 py-1.5">Payout</th>}
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr
                  key={j.id}
                  className="cursor-pointer border-t border-line hover:bg-surface-hover"
                  onClick={() => j.invoiceId && nav(`/invoices/${j.invoiceId}`)}
                  title="Open the order"
                >
                  <td className="px-3 py-1.5 font-semibold text-content">{j.orderNo}</td>
                  <td className="px-3 py-1.5 text-content-muted">{j.customerName || '—'}</td>
                  <td className="px-3 py-1.5">
                    {j.itemName} <span className="text-content-subtle">× {j.qty}</span>
                    {j.alteration && <span className="ml-1 text-2xs font-semibold text-warning-700">Alteration</span>}
                  </td>
                  <td className="px-3 py-1.5 text-content-muted">{WORK_LABEL[j.workType] || j.workType}</td>
                  <td className="px-3 py-1.5">
                    <span className={`rounded-full px-2 py-0.5 text-2xs font-semibold ${JOB_TONE[j.status]}`}>{JOB_LABEL[j.status]}</span>
                  </td>
                  <td className="px-3 py-1.5 text-content-muted">
                    {finished ? (j.completedAt ? fmtDate(j.completedAt) : '—') : (j.deliveryDate ? fmtDate(j.deliveryDate) : '—')}
                  </td>
                  {money && (
                    <td className="px-3 py-1.5 text-right font-semibold tabular-nums">
                      {inr(j.cost)}
                      {j.overrideCost != null && <span className="ml-1 text-2xs font-normal text-content-subtle">(override)</span>}
                    </td>
                  )}
                  {money && (
                    <td className="px-3 py-1.5 text-xs">
                      {j.settled && j.settlementStatus === 'POSTED'
                        ? <span className="font-semibold text-success-700">Paid · {j.settlementNo}</span>
                        : <span className="text-danger-600">Unpaid</span>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ---------------- payouts ---------------- */
function Payouts({ settlements, unpaid }) {
  const nav = useNavigate();
  return (
    <section className="card overflow-hidden">
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        <h2 className="mr-auto text-xs font-bold uppercase tracking-wider text-content">Payouts</h2>
        <span className="text-xs text-content-muted">Unpaid right now: <b className="text-danger-600">{inr(unpaid)}</b></span>
        <button className="btn-outline btn-sm" onClick={() => nav('/finance/settlements')}>Open Worker Payments</button>
      </header>
      {settlements.length === 0 ? (
        <p className="py-6 text-center text-sm italic text-content-subtle">No payouts posted yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-surface-sunken text-left text-2xs font-bold uppercase tracking-wide text-content-muted">
            <tr>
              <th className="px-3 py-1.5">Payout No.</th>
              <th className="px-3 py-1.5">Date</th>
              <th className="px-3 py-1.5">Jobs</th>
              <th className="px-3 py-1.5">Mode</th>
              <th className="px-3 py-1.5">Status</th>
              <th className="px-3 py-1.5 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {settlements.map((s) => (
              <tr key={s.id} className="border-t border-line">
                <td className="px-3 py-1.5 font-semibold">{s.settlementNo}</td>
                <td className="px-3 py-1.5 text-content-muted">{fmtDate(s.createdAt)}</td>
                <td className="px-3 py-1.5 text-content-muted">{s.lineCount}</td>
                <td className="px-3 py-1.5 text-content-muted">{s.paymentMode || '—'}</td>
                <td className="px-3 py-1.5">
                  {s.status === 'POSTED'
                    ? <span className="text-2xs font-semibold text-success-700">Posted</span>
                    : <span className="text-2xs font-semibold text-danger-600">Reversed</span>}
                </td>
                <td className="px-3 py-1.5 text-right font-semibold tabular-nums">{inr(s.netAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/* ---------------- rate card ---------------- */
function RateCard({ rows, workerId, isAdmin, workerWiseRates }) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState(null); // { itemId, itemName, workType, rateType, rateValue }
  const [del, setDel] = useState(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ['worker-overview', String(workerId)] });

  const save = useMutation({
    mutationFn: (body) => saveWorkerRate(workerId, body),
    onSuccess: (d) => { success(d.message || 'Rate saved.'); setEdit(null); refresh(); },
    onError: (e) => error(e.response?.data?.message || 'Could not save the rate.'),
  });
  const remove = useMutation({
    mutationFn: (rateId) => deleteWorkerRate(workerId, rateId),
    onSuccess: (d) => { success(d.message || 'Rate removed.'); setDel(null); refresh(); },
    onError: (e) => error(e.response?.data?.message || 'Could not remove the rate.'),
  });

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? rows.filter((r) => r.itemName.toLowerCase().includes(s)) : rows;
  }, [rows, q]);

  const cell = (row, side) => {
    const r = row[side];
    const workType = side === 'cutting' ? 'CUTTING' : 'STITCHING';
    return (
      <td className="px-3 py-1.5">
        <div className="flex items-center gap-1.5">
          <span className="font-semibold tabular-nums">{inr(r.effectiveRate)}</span>
          {r.source === 'worker' ? (
            <span className="rounded bg-brand-50 px-1.5 py-0.5 text-2xs font-semibold text-brand-700">
              own{r.rateType === 'PERCENTAGE' ? ` ${r.rateValue}%` : ''}
            </span>
          ) : (
            <span className="text-2xs text-content-subtle">standard</span>
          )}
          {isAdmin && (
            <>
              <button
                className="btn-icon h-6 w-6"
                title={r.rateId ? 'Change this worker\'s rate' : 'Set a rate for this worker'}
                onClick={() => setEdit({
                  itemId: row.itemId, itemName: row.itemName, workType,
                  rateType: r.rateType || 'FIXED',
                  rateValue: r.rateValue != null ? String(r.rateValue) : String(r.standardRate || ''),
                })}
              >
                <Pencil size={13} />
              </button>
              {r.rateId && (
                <button
                  className="btn-icon h-6 w-6 hover:bg-danger-50 hover:text-danger-600"
                  title="Back to the item's standard rate"
                  onClick={() => setDel({ rateId: r.rateId, itemName: row.itemName, workType })}
                >
                  <Trash2 size={13} />
                </button>
              )}
            </>
          )}
        </div>
        {r.source === 'worker' && r.standardRate !== r.effectiveRate && (
          <p className="text-2xs text-content-subtle">standard {inr(r.standardRate)}</p>
        )}
      </td>
    );
  };

  return (
    <section className="card overflow-hidden">
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        <h2 className="mr-auto text-xs font-bold uppercase tracking-wider text-content">Rate card</h2>
        <div className="relative">
          <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input className="input input-sm w-48 pl-7" placeholder="Find item…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </header>

      {!workerWiseRates && (
        <p className="border-b border-line bg-warning-50 px-3 py-2 text-xs text-warning-700">
          Worker-wise rates are switched off for this company, so every job is costed at the item's standard rate.
          Rates saved here are kept and start applying as soon as the setting is turned on (Settings → worker-wise rates).
        </p>
      )}
      <p className="border-b border-line px-3 py-1.5 text-2xs text-content-subtle">
        Changing a rate affects new work only — a job already assigned keeps the rate it was costed at.
      </p>

      <div className="max-h-[32rem] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-surface-sunken text-left text-2xs font-bold uppercase tracking-wide text-content-muted">
            <tr>
              <th className="px-3 py-1.5">Item</th>
              <th className="px-3 py-1.5 text-right">Customer price</th>
              <th className="px-3 py-1.5">Cutting</th>
              <th className="px-3 py-1.5">Stitching</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-6 text-center italic text-content-subtle">No items match.</td></tr>
            ) : list.map((row) => (
              <tr key={row.itemId} className="border-t border-line">
                <td className="px-3 py-1.5 font-medium text-content">{row.itemName}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-content-muted">{inr(row.customerRate)}</td>
                {cell(row, 'cutting')}
                {cell(row, 'stitching')}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEdit(null)}>
          <div className="card w-full max-w-sm p-4" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-start justify-between gap-2">
              <div>
                <h3 className="text-base font-bold text-content">{edit.itemName}</h3>
                <p className="text-xs text-content-muted">{WORK_LABEL[edit.workType]} rate for this worker</p>
              </div>
              <button className="btn-icon" onClick={() => setEdit(null)}><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <span className="mb-1 block text-xs font-semibold text-content-muted">Rate type</span>
                <div className="flex gap-2">
                  {[['FIXED', '₹ per piece'], ['PERCENTAGE', '% of customer price']].map(([v, l]) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setEdit((x) => ({ ...x, rateType: v }))}
                      className={`flex-1 rounded-md border px-2 py-1.5 text-xs font-semibold ${edit.rateType === v ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-line text-content-muted hover:bg-surface-hover'}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-content-muted">
                  {edit.rateType === 'PERCENTAGE' ? 'Percentage (0–100)' : 'Amount per piece (₹)'}
                </span>
                <input
                  type="number" min={0} step="0.01" className="input text-right font-semibold"
                  value={edit.rateValue} autoFocus
                  onChange={(e) => setEdit((x) => ({ ...x, rateValue: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') save.mutate(edit); }}
                />
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-outline" onClick={() => setEdit(null)}>Cancel</button>
              <button className="btn-primary" disabled={save.isPending || edit.rateValue === ''} onClick={() => save.mutate(edit)}>
                <Check size={15} /> {save.isPending ? 'Saving…' : 'Save rate'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!del}
        onClose={() => setDel(null)}
        title="Remove this rate"
        message={`${del?.itemName} — ${WORK_LABEL[del?.workType] || ''}: go back to the item's standard rate for this worker?`}
        busy={remove.isPending}
        onConfirm={() => remove.mutate(del.rateId)}
      />
    </section>
  );
}
